import React, { useEffect, useState, useRef } from 'react';
import { message } from '../Message';
import axios from 'axios';
import Spinner from '../Spinner';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { CircularProgress } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';

import { styled } from '@mui/material/styles';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

const StyleCard = styled('div')(({ theme }) => ({
  padding: '15px',
  borderBottom: '1px solid #eee',
  marginBottom: '10px',
  '&:last-child': {
    borderBottom: 'none',
  },
  '& h3': {
    margin: '0 0 10px 0',
    fontSize: '1.1rem',
    fontWeight: 500,
  },
  '& .description': {
    margin: '0 0 10px 0',
    color: '#666',
  },
  '& .metadata': {
    fontSize: '0.9em',
    color: '#888',
    '& p': {
      margin: '5px 0',
    },
  },
  '& img': {
    maxWidth: '200px',
    height: 'auto',
    display: 'block',
    marginBottom: '10px',
    borderRadius: '4px',
  },
}));

const LoadingContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  padding: '20px',
});

const ScrollableContent = styled('div')({
  maxHeight: '400px',
  overflow: 'auto',
  padding: '10px',
});

const PromptsDialog = ({ open, onClose, title, styleIds, firebase }) => {
  const [styles, setStyles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      loadStyles();
    }
  }, [open]);

  const loadStyles = async () => {
    if (!Array.isArray(styleIds) || styleIds.length === 0) {
      setError('Empty');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const stylePromises = styleIds.map(id => firebase.getPhotoStyle(id));
      const styleSnapshots = await Promise.all(stylePromises);

      const styleData = styleSnapshots
        .map(doc => {
          if (!doc.exists) return null;
          return {
            id: doc.id,
            ...doc.data()
          };
        })
        .filter(style => style !== null);

      setStyles(styleData);
    } catch (error) {
      console.error('Error fetching styles:', error);
      setError('Failed to load styles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStyles([]);
    setError(null);
    onClose();
  };

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setStyles([]);
      setError(null);
    }
  }, [open]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate();
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {error ? (
          <div style={{ color: 'red', textAlign: 'center', padding: '20px' }}>
            {error}
          </div>
        ) : styles.length === 0 && !loading ? (
          <LoadingContainer>
            <Button
              variant="contained"
              color="primary"
              onClick={loadStyles}
              disabled={!styleIds?.length}
            >
              Load Styles ({styleIds?.length || 0})
            </Button>
          </LoadingContainer>
        ) : loading ? (
          <LoadingContainer>
            <CircularProgress />
          </LoadingContainer>
        ) : styles.length > 0 ? (
          <ScrollableContent>
            {styles.map((style) => (
              <StyleCard key={style.id}>
                <h3>{style.title || 'Untitled Style'}</h3>
                <p className="description">{style.description || 'No description available'}</p>
                {style.image && (
                  <img
                    src={style.image}
                    alt={style.title}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'placeholder-image-url';
                    }}
                  />
                )}
                <div className="metadata">
                  {style.prompt && <p>Prompt: {style.prompt}</p>}
                  <p>Status: {style.active ? '✅ Active' : '❌ Inactive'}</p>
                </div>
              </StyleCard>
            ))}
          </ScrollableContent>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            No styles available
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {styles.length > 0 && (
          <Button
            onClick={() => setStyles([])}
            color="secondary"
          >
            Clear
          </Button>
        )}
        <Button onClick={handleClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

const VersionSelector = ({ onVersionChange }) => {
  return (
    <FormControl sx={{ minWidth: 120, marginBottom: 2 }}>
      <InputLabel id="version-select-label">Version</InputLabel>
      <Select
        labelId="version-select-label"
        id="version-select"
        defaultValue="v1"
        label="Version"
        onChange={e => onVersionChange(e.target.value)}
      >
        <MenuItem value="v1">Version 1</MenuItem>
        <MenuItem value="v2">Version 2</MenuItem>
      </Select>
    </FormControl>
  );
};

const PhotoPacks = ({ t, isCreating, firebase, onFetchUser, userData }) => {
  const scrollableRef = useRef(null);

  const [showShopFurniture, setShowShopFurniture] = useState(false);
  const [imageShop, setImageShop] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);

  const [rows, setRows] = useState([]);
  const [rowsV2, setRowsV2] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openAddV2, setOpenAddV2] = useState(false);
  const [mode, setMode] = useState('');
  const [removeId, setRemoveId] = useState('');
  const [openCompare, setOpenCompare] = useState(false);
  const [compareImages, setCompareImages] = useState({});
  const [editData, setEditData] = useState(null); // Add state for edit data
  const fileInputRef = useRef(null); // Create a ref for the file input
  const [isUploading, setIsUploading] = useState(false);
  const [addData, setAddData] = useState(null); // Add state for edit data
  const [version, setVersion] = useState('v1');

  const [openMalePrompts, setOpenMalePrompts] = useState(false);
  const [openFemalePrompts, setOpenFemalePrompts] = useState(false);
  const [selectedPrompts, setSelectedPrompts] = useState([]);

  const columns = [
    { id: 'action', label: 'Actions', minWidth: 40 },
    { id: 'id', label: 'ID', minWidth: 30 },
    { id: 'showOnLanding', label: 'Show', minWidth: 30 },
    { id: 'isHot', label: 'Hot', minWidth: 50 },
    { id: 'title', label: 'Title', minWidth: 50 },
    { id: 'description', label: 'Description', minWidth: 50 },
    { id: 'image', label: 'Thumbnail', minWidth: 50 },
    { id: 'prompt', label: 'Prompt', minWidth: 50 },
    { id: 'category', label: 'Category', minWidth: 50 },
    { id: 'active', label: 'Active', minWidth: 50 },
    { id: 'createdAt', label: 'Created At', minWidth: 100 },
  ];

  const columnsV2 = [
    { id: 'action', label: 'Actions', minWidth: 40 },
    {
      id: 'type',
      label: 'Type',
      type: 'select',
      options: ['ai_photography', 'ai_headshot'],
    },
    { id: 'id', label: 'ID', minWidth: 30 },
    { id: 'showOnLanding', label: 'Show', minWidth: 30 },
    { id: 'isHot', label: 'Hot', minWidth: 50 },
    { id: 'title', label: 'Title', minWidth: 50 },
    { id: 'description', label: 'Description', minWidth: 50 },
    { id: 'image', label: 'Thumbnail', minWidth: 50 },
    { id: 'malePrompts', label: 'Male Prompts', minWidth: 50 },
    { id: 'femalePrompts', label: 'Female Prompts', minWidth: 50 },
    {
      id: 'category',
      label: 'Category',
      type: 'multiselect',
      options: ['trending', 'new', 'popular', 'for_creator', 'for_business', 'other'],
    },
    { id: 'active', label: 'Active', minWidth: 50 },
    { id: 'createdAt', label: 'Created At', minWidth: 100 },
  ];

  const handleEdit = async itemToEdit => {
    setEditData(itemToEdit); // Set the item data to edit
    setOpen(true); // Open the dialog
  };

  const handleEditV2 = async itemToEdit => {
    const editDataFormatted = {
      ...itemToEdit,
      malePrompts: Array.isArray(itemToEdit.malePrompts) ? itemToEdit.malePrompts.join('\n') : itemToEdit.malePrompts,
      femalePrompts: Array.isArray(itemToEdit.femalePrompts) ? itemToEdit.femalePrompts.join('\n') : itemToEdit.femalePrompts,
    };
    setEditData(editDataFormatted);
    setOpen(true);
  };

  useEffect(() => {
    firebase
      .getPhotoPacks()
      .then(result => {
        const jobsArray = result.docs.map(doc => ({
          photoId: doc.id,
          ...doc.data(),
        }));

        const updatedData = jobsArray.map(item => {
          const date = new Date(item.createdAt);
          const gmtOffset = 7; // GMT+7
          date.setHours(date.getHours() + gmtOffset);
          const formattedDate = `${date.toISOString().substr(11, 8)} ${date.toISOString().substr(8, 2)}-${date
            .toISOString()
            .substr(5, 2)}-${date.toISOString().substr(0, 4)}`;

          return {
            ...item,
            createdAt: formattedDate,
            action: (
              <>
                <Button
                  style={{ marginRight: '2px', marginBottom: '2px' }}
                  variant="outlined"
                  color="success"
                  onClick={() => handleEdit(item)}
                >
                  Edit
                </Button>
              </>
            ),
            image: (
              <>
                <img src={item.image} style={{ width: '200px', cursor: 'pointer' }} onClick={e => onOpenCompare(e, item)}></img>
              </>
            ),
          };
        });

        setRows(updatedData);
      })
      .catch(error => {
        console.log(error);
      });

    firebase
      .getPhotoPacksV2()
      .then(result => {
        const jobsArray = result.docs.map(doc => ({
          photoId: doc.id,
          ...doc.data(),
        }));

        const updatedData = jobsArray.map(item => {
          const date = new Date(item.createdAt);
          const gmtOffset = 7; // GMT+7
          date.setHours(date.getHours() + gmtOffset);
          const formattedDate = `${date.toISOString().substr(11, 8)} ${date.toISOString().substr(8, 2)}-${date
            .toISOString()
            .substr(5, 2)}-${date.toISOString().substr(0, 4)}`;

          return {
            ...item,
            createdAt: formattedDate,
            action: (
              <>
                <Button
                  style={{ marginRight: '2px', marginBottom: '2px' }}
                  variant="outlined"
                  color="success"
                  onClick={() => handleEditV2(item)}
                >
                  Edit
                </Button>
              </>
            ),
            image: (
              <>
                <img src={item.image} style={{ width: '200px', cursor: 'pointer' }} onClick={e => onOpenCompare(e, item)}></img>
              </>
            ),
          };
        });

        setRowsV2(updatedData);
      })
      .catch(error => {
        console.log(error);
      });

    return () => { };
  }, []);

  const handleClose = () => {
    setOpen(false);
    setOpenAdd(false);
    setOpenAddV2(false);
    setMode('');
  };

  const handleOk = async () => {
    // Validate data
    if (!editData.title || !editData.description || !editData.prompt || !editData.category || !editData.image) {
      message.error('Please fill in all fields.');
      return;
    }

    try {
      const photoId = editData.photoId;
      const updatedData = {
        active: editData.active,
        isHot: editData.isHot,
        showOnLanding: editData.showOnLanding,
        title: editData.title,
        description: editData.description,
        prompt: editData.prompt,
        category: editData.category,
        image: editData.image,
      };
      await firebase.updatePhotoPack(photoId, updatedData);

      firebase
        .getPhotoPacks()
        .then(result => {
          const jobsArray = result.docs.map(doc => ({
            photoId: doc.id,
            ...doc.data(),
          }));

          const updatedData = jobsArray.map(item => {
            const date = new Date(item.createdAt);
            const gmtOffset = 7; // GMT+7
            date.setHours(date.getHours() + gmtOffset);
            const formattedDate = `${date.toISOString().substr(11, 8)} ${date.toISOString().substr(8, 2)}-${date
              .toISOString()
              .substr(5, 2)}-${date.toISOString().substr(0, 4)}`;

            return {
              ...item,
              createdAt: formattedDate,
              action: (
                <>
                  <Button
                    style={{ marginRight: '2px', marginBottom: '2px' }}
                    variant="outlined"
                    color="success"
                    onClick={() => handleEdit(item)}
                  >
                    Edit
                  </Button>
                </>
              ),
              image: (
                <>
                  <img src={item.image} style={{ width: '200px', cursor: 'pointer' }} onClick={e => onOpenCompare(e, item)}></img>
                </>
              ),
            };
          });

          setRows(updatedData);
        })
        .catch(error => {
          console.log(error);
        });

      handleClose();
      message.success('Photo pack updated successfully!');
    } catch (error) {
      console.error('Error saving data:', error);
    }
  };

  const handleOkV2 = async () => {
    // Validate data
    if (
      !editData.title ||
      !editData.description ||
      !editData.malePrompts ||
      !editData.femalePrompts ||
      !editData.category ||
      !editData.image ||
      !editData.type
    ) {
      message.error('Please fill in all fields.');
      return;
    }

    try {
      const photoId = editData.photoId;
      const updatedData = {
        type: editData.type,
        active: editData.active,
        isHot: editData.isHot,
        showOnLanding: editData.showOnLanding,
        title: editData.title,
        description: editData.description,
        malePrompts: editData.malePrompts.split('\n').filter(line => line.trim()),
        femalePrompts: editData.femalePrompts.split('\n').filter(line => line.trim()),
        category: editData.category,
        image: editData.image,
      };
      await firebase.updatePhotoPackV2(photoId, updatedData);

      firebase
        .getPhotoPacksV2()
        .then(result => {
          const jobsArray = result.docs.map(doc => ({
            photoId: doc.id,
            ...doc.data(),
          }));

          const updatedData = jobsArray.map(item => {
            const date = new Date(item.createdAt);
            const gmtOffset = 7; // GMT+7
            date.setHours(date.getHours() + gmtOffset);
            const formattedDate = `${date.toISOString().substr(11, 8)} ${date.toISOString().substr(8, 2)}-${date
              .toISOString()
              .substr(5, 2)}-${date.toISOString().substr(0, 4)}`;

            return {
              ...item,
              createdAt: formattedDate,
              action: (
                <>
                  <Button
                    style={{ marginRight: '2px', marginBottom: '2px' }}
                    variant="outlined"
                    color="success"
                    onClick={() => handleEditV2(item)}
                  >
                    Edit
                  </Button>
                </>
              ),
              image: (
                <>
                  <img src={item.image} style={{ width: '200px', cursor: 'pointer' }} onClick={e => onOpenCompare(e, item)}></img>
                </>
              ),
            };
          });

          setRowsV2(updatedData);
        })
        .catch(error => {
          console.log(error);
        });

      handleClose();
      message.success('Photo pack updated successfully!');
    } catch (error) {
      console.error('Error saving data:', error);
    }
  };

  const handleAddNew = async () => {
    if (!addData.id || !addData.title || !addData.description || !addData.prompt || !addData.category || !addData.image) {
      message.error('Please fill in all fields.');
      return;
    }

    try {
      const newData = {
        id: addData.id,
        active: addData.active,
        isHot: addData.isHot,
        showOnLanding: addData.showOnLanding,
        title: addData.title,
        description: addData.description,
        prompt: addData.prompt,
        category: [addData.category],
        image: addData.image,
        createdAt: new Date().toISOString(),
      };

      console.log(newData);
      await firebase.addNewPhotoPack(newData);

      firebase
        .getPhotoPacks()
        .then(result => {
          const jobsArray = result.docs.map(doc => ({
            photoId: doc.id,
            ...doc.data(),
          }));

          const updatedData = jobsArray.map(item => {
            const date = new Date(item.createdAt);
            const gmtOffset = 7; // GMT+7
            date.setHours(date.getHours() + gmtOffset);
            const formattedDate = `${date.toISOString().substr(11, 8)} ${date.toISOString().substr(8, 2)}-${date
              .toISOString()
              .substr(5, 2)}-${date.toISOString().substr(0, 4)}`;

            return {
              ...item,
              createdAt: formattedDate,
              action: (
                <>
                  <Button
                    style={{ marginRight: '2px', marginBottom: '2px' }}
                    variant="outlined"
                    color="success"
                    onClick={() => handleEdit(item)}
                  >
                    Edit
                  </Button>
                </>
              ),
              image: (
                <>
                  <img src={item.image} style={{ width: '200px', cursor: 'pointer' }} onClick={e => onOpenCompare(e, item)}></img>
                </>
              ),
            };
          });

          setRows(updatedData);
          setAddData(null);
        })
        .catch(error => {
          console.log(error);
        });

      handleClose();
      message.success('Photo pack updated successfully!');
    } catch (error) {
      console.error('Error saving data:', error);
    }
  };

  const handleAddNewV2 = async () => {
    // Validate required fields
    if (
      !addData?.id ||
      !addData?.title ||
      !addData?.description ||
      // !addData?.malePrompts ||
      // !addData?.femalePrompts ||
      !addData?.category ||
      !addData?.image ||
      !addData?.type
    ) {
      message.error('Please fill in all required fields.');
      return;
    }

    try {
      const newData = {
        id: addData.id,
        type: addData.type,
        active: addData.active || false,
        isHot: addData.isHot || false,
        showOnLanding: addData.showOnLanding || false,
        title: addData.title,
        description: addData.description,
        malePrompts: addData.malePrompts ? addData.malePrompts.split('\n').filter(line => line.trim()) : [],
        femalePrompts: addData.femalePrompts ? addData.femalePrompts.split('\n').filter(line => line.trim()) : [],
        category: [addData.category],
        image: addData.image,
        createdAt: new Date().toISOString(),
      };

      await firebase.addNewPhotoPackV2(newData);

      // Refresh the data
      const result = await firebase.getPhotoPacksV2();
      const jobsArray = result.docs.map(doc => ({
        photoId: doc.id,
        ...doc.data(),
      }));

      const updatedData = jobsArray.map(item => {
        const date = new Date(item.createdAt);
        const gmtOffset = 7; // GMT+7
        date.setHours(date.getHours() + gmtOffset);
        const formattedDate = `${date.toISOString().substr(11, 8)} ${date.toISOString().substr(8, 2)}-${date
          .toISOString()
          .substr(5, 2)}-${date.toISOString().substr(0, 4)}`;

        return {
          ...item,
          createdAt: formattedDate,
          action: (
            <Button
              style={{ marginRight: '2px', marginBottom: '2px' }}
              variant="outlined"
              color="success"
              onClick={() => handleEditV2(item)}
            >
              Edit
            </Button>
          ),
          image: (
            <img src={item.image} style={{ width: '200px', cursor: 'pointer' }} onClick={e => onOpenCompare(e, item)} alt="Thumbnail" />
          ),
        };
      });

      setRowsV2(updatedData);
      setAddData(null);
      handleClose();
      message.success('Photo pack added successfully!');
    } catch (error) {
      console.error('Error saving data:', error);
      message.error('Failed to add photo pack. Please try again.');
    }
  };

  function convertStatus(status) {
    switch (status) {
      case false:
        return 'Waiting';
        break;
      case true:
        return 'Done';
        break;

      default:
        return '';
        break;
    }
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  function generateRandomFileName(extension) {
    const randomNumber = Math.floor(Math.random() * 1000000000); // Generate a random number between 0 and 999999999
    return `noithatai-${randomNumber}.${extension}`;
  }

  const onFindAgency = image => {
    setOpenForm(true);
    setImageShop(image);
    setShowShopFurniture(false);
  };

  const onCloseShopFurniture = () => {
    setShowShopFurniture(false);
  };

  const handleDelete = async id => {
    setOpen(true);
    setMode('disapprove');
    setRemoveId(id);
  };

  const onOpenCompare = (e, item) => {
    console.log(item);
    e.preventDefault();
    setCompareImages({
      before: item.originalUrl,
      after: item.upscaleUrl,
    });
    setOpenCompare(true);
  };

  const handleImageUpload = async file => {
    if (!file) return; // Ensure a file is selected

    try {
      setIsUploading(true);
      const downloadURL = await firebase.uploadAdminFile(file); // Use the uploadAdminFile method

      setEditData({ ...editData, image: downloadURL }); // Update editData with the new image URL
      setAddData({ ...addData, image: downloadURL }); // Update editData with the new image URL

      message.success('Image uploaded successfully!'); // Notify success
      setIsUploading(false);
    } catch (error) {
      message.error('Image upload failed!'); // Notify failure
      console.error(error);
      setIsUploading(false);
    }
  };

  const addNewPhotoPack = () => {
    setEditData(null);
    setOpenAdd(true);
  };

  const addNewPhotoPackV2 = () => {
    setEditData(null);
    setOpenAddV2(true);
  };

  const onVersionChange = version => {
    setVersion(version);
  };

  return (
    <>
      <div className="my-design" ref={scrollableRef}>
        <h3>Manage Photo Packs</h3>
        <p style={{ color: '#8c8c8c', fontSize: '14px', marginTop: '0', marginBottom: '1rem' }}>{'Only admin can do.'}</p>
        <div>
          <VersionSelector onVersionChange={onVersionChange} />
        </div>
        <Button className="mb-2 mr-2" style={{ marginRight: '4px' }} variant="outlined" color="success" onClick={() => addNewPhotoPack()}>
          Add new photo pack v1
        </Button>
        <Button className="mb-2" style={{ marginRight: '4px' }} variant="outlined" color="success" onClick={() => addNewPhotoPackV2()}>
          Add new photo pack v2
        </Button>
        {version === 'v1' ? (
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    {columns.map(column => (
                      <TableCell key={column.id} align={column.align} style={{ minWidth: column.minWidth }}>
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(row => {
                    return (
                      <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                        {columns.map(column => {
                          const value = row[column.id];
                          return (
                            <TableCell key={column.id} align={column.align}>
                              {column.id === 'category'
                                ? Array.isArray(value)
                                  ? value.join(', ')
                                  : value
                                : column.id === 'showOnLanding' || column.id === 'isHot' || column.id === 'active'
                                  ? value
                                    ? '✅'
                                    : '❌'
                                  : value}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        ) : (
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    {columnsV2.map(column => (
                      <TableCell key={column.id} align={column.align} style={{ minWidth: column.minWidth }}>
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rowsV2.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(row => {
                    return (
                      <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                        {columnsV2.map(column => {
                          const value = row[column.id];
                          return (
                            <TableCell key={column.id} align={column.align}>
                              {column.id === 'malePrompts' ? (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    setSelectedPrompts(Array.isArray(value) ? value : []);
                                    setOpenMalePrompts(true);
                                  }}
                                >
                                  View ({Array.isArray(value) ? value.length : 0})
                                </Button>
                              ) : column.id === 'femalePrompts' ? (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    setSelectedPrompts(Array.isArray(value) ? value : []);
                                    setOpenFemalePrompts(true);
                                  }}
                                >
                                  View ({Array.isArray(value) ? value.length : 0})
                                </Button>
                              ) : column.id === 'category' ? (
                                Array.isArray(value) ? (
                                  value.join(', ')
                                ) : (
                                  value
                                )
                              ) : column.id === 'showOnLanding' || column.id === 'isHot' || column.id === 'active' ? (
                                value ? (
                                  '✅'
                                ) : (
                                  '❌'
                                )
                              ) : (
                                value
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={rowsV2.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        )}
      </div>

      {/* Edit Photo Pack Dialog */}
      <Dialog
        open={open && editData && version === 'v1'}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit photo pack</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>ID:</label>
              <TextField
                value={editData?.id || ''}
                disabled
                fullWidth
                variant="outlined"
                margin="normal"
              />
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Show on Home page:</label>
              <TextField
                select
                value={editData?.showOnLanding}
                onChange={(e) => setEditData({ ...editData, showOnLanding: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              >
                <MenuItem value={true}>✅</MenuItem>
                <MenuItem value={false}>❌</MenuItem>
              </TextField>
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Hot:</label>
              <TextField
                select
                value={editData?.isHot}
                onChange={(e) => setEditData({ ...editData, isHot: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              >
                <MenuItem value={true}>✅</MenuItem>
                <MenuItem value={false}>❌</MenuItem>
              </TextField>
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Title:</label>
              <TextField
                value={editData?.title || ''}
                onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              />
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Description:</label>
              <TextField
                value={editData?.description || ''}
                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              />
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Prompt:</label>
              <TextField
                value={editData?.prompt || ''}
                onChange={(e) => setEditData({ ...editData, prompt: e.target.value })}
                fullWidth
                multiline
                rows={10}
                variant="outlined"
                margin="normal"
              />
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Image:</label>
              <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                {isUploading ? (
                  <CircularProgress size={12} color="inherit" style={{ marginRight: '6px' }} />
                ) : (
                  editData?.image && (
                    <img src={editData.image} style={{ width: "200px", cursor: "pointer" }} alt="Thumbnail" />
                  )
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    await handleImageUpload(file);
                  }}
                  style={{ marginTop: '10px', display: 'none' }}
                  ref={fileInputRef}
                />
                <Button
                  style={{ marginTop: '10px' }}
                  variant="outlined"
                  onClick={() => fileInputRef.current.click()}
                >
                  Change Thumbnail
                </Button>
              </div>
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Category:</label>
              <TextField
                value={editData?.category || ''}
                onChange={(e) => setEditData({ ...editData, category: [e.target.value] })}
                fullWidth
                variant="outlined"
                margin="normal"
              />
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Active:</label>
              <TextField
                select
                value={editData?.active}
                onChange={(e) => setEditData({ ...editData, active: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              >
                <MenuItem value={true}>✅</MenuItem>
                <MenuItem value={false}>❌</MenuItem>
              </TextField>
            </div>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="outlined">
            Cancel
          </Button>
          <Button
            disabled={isUploading}
            onClick={handleOk}
            color="primary"
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Photo Pack V2 Dialog */}
      <Dialog
        open={open && editData && version === 'v2'}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit photo pack V2</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>ID:</label>
              <TextField
                value={editData?.id || ''}
                disabled
                fullWidth
                variant="outlined"
                margin="normal"
              />
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Type:</label>
              <TextField
                select
                value={editData?.type || ''}
                onChange={(e) => setEditData({ ...editData, type: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              >
                <MenuItem value="ai_photography">AI Photography</MenuItem>
                <MenuItem value="ai_headshot">AI Headshot</MenuItem>
              </TextField>
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Show on Home page:</label>
              <TextField
                select
                value={editData?.showOnLanding}
                onChange={(e) => setEditData({ ...editData, showOnLanding: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              >
                <MenuItem value={true}>✅</MenuItem>
                <MenuItem value={false}>❌</MenuItem>
              </TextField>
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Hot:</label>
              <TextField
                select
                value={editData?.isHot}
                onChange={(e) => setEditData({ ...editData, isHot: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              >
                <MenuItem value={true}>✅</MenuItem>
                <MenuItem value={false}>❌</MenuItem>
              </TextField>
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Title:</label>
              <TextField
                value={editData?.title || ''}
                onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              />
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Description:</label>
              <TextField
                value={editData?.description || ''}
                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              />
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Male Prompts:</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setSelectedPrompts(Array.isArray(editData?.malePrompts) ? editData.malePrompts : []);
                    setOpenMalePrompts(true);
                  }}
                >
                  View Prompts ({Array.isArray(editData?.malePrompts) ? editData.malePrompts.length : 0})
                </Button>
              </div>
              <TextField
                value={editData?.malePrompts || ''}
                onChange={(e) => setEditData({ ...editData, malePrompts: e.target.value })}
                fullWidth
                multiline
                rows={10}
                variant="outlined"
                margin="normal"
                placeholder="Enter each prompt on a new line"
              />
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Female Prompts:</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setSelectedPrompts(Array.isArray(editData?.femalePrompts) ? editData.femalePrompts : []);
                    setOpenFemalePrompts(true);
                  }}
                >
                  View Prompts ({Array.isArray(editData?.femalePrompts) ? editData.femalePrompts.length : 0})
                </Button>
              </div>
              <TextField
                value={editData?.femalePrompts || ''}
                onChange={(e) => setEditData({ ...editData, femalePrompts: e.target.value })}
                fullWidth
                multiline
                rows={10}
                variant="outlined"
                margin="normal"
                placeholder="Enter each prompt on a new line"
              />
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Image:</label>
              <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                {isUploading ? (
                  <CircularProgress size={12} color="inherit" style={{ marginRight: '6px' }} />
                ) : (
                  editData?.image && (
                    <img src={editData.image} style={{ width: "200px", cursor: "pointer" }} alt="Thumbnail" />
                  )
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    await handleImageUpload(file);
                  }}
                  style={{ marginTop: '10px', display: 'none' }}
                  ref={fileInputRef}
                />
                <Button
                  style={{ marginTop: '10px' }}
                  variant="outlined"
                  onClick={() => fileInputRef.current.click()}
                >
                  Change Thumbnail
                </Button>
              </div>
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Categories:</label>
              <FormControl fullWidth variant="outlined" margin="normal">
                <Select
                  multiple
                  value={editData?.category || []}
                  onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                  renderValue={(selected) => selected.join(', ')}
                >
                  <MenuItem value="trending">Trending</MenuItem>
                  <MenuItem value="new">New</MenuItem>
                  <MenuItem value="popular">Popular</MenuItem>
                  <MenuItem value="for_creator">For Creator</MenuItem>
                  <MenuItem value="for_business">For Business</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Active:</label>
              <TextField
                select
                value={editData?.active}
                onChange={(e) => setEditData({ ...editData, active: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              >
                <MenuItem value={true}>✅</MenuItem>
                <MenuItem value={false}>❌</MenuItem>
              </TextField>
            </div>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="outlined">
            Cancel
          </Button>
          <Button
            disabled={isUploading}
            onClick={handleOkV2}
            color="primary"
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAdd} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add new photo pack</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>ID:</label>
              <TextField
                value={addData?.id || ''}
                onChange={(e) => setAddData({ ...addData, id: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Show on Home page:</label>
              <TextField
                select
                value={addData?.showOnLanding}
                onChange={(e) => setAddData({ ...addData, showOnLanding: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              >
                <MenuItem value={true}>✅</MenuItem>
                <MenuItem value={false}>❌</MenuItem>
              </TextField>
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Hot:</label>
              <TextField
                select
                value={addData?.isHot}
                onChange={(e) => setAddData({ ...addData, isHot: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              >
                <MenuItem value={true}>✅</MenuItem>
                <MenuItem value={false}>❌</MenuItem>
              </TextField>
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Title:</label>
              <TextField
                value={addData?.title || ''}
                onChange={(e) => setAddData({ ...addData, title: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Description:</label>
              <TextField
                value={addData?.description || ''}
                onChange={(e) => setAddData({ ...addData, description: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Prompt:</label>
              <TextField
                value={addData?.prompt || ''}
                onChange={(e) => setAddData({ ...addData, prompt: e.target.value })}
                fullWidth
                multiline
                rows={10}
                variant="outlined"
                margin="normal"
              />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Image:</label>
              <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                {isUploading ?
                  <>
                    <CircularProgress size={12} color="inherit" style={{ marginRight: '6px' }} />
                  </>
                  : (
                    <>
                      <img src={addData?.image} style={{ width: "200px", cursor: "pointer" }}></img>
                    </>
                  )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0]; // Get the selected file
                    // setAddData({ ...addData, image: file }); // Store the file in editData
                    await handleImageUpload(file);
                  }}
                  style={{ marginTop: '10px', display: 'none' }}
                  ref={fileInputRef}
                />
                <Button
                  style={{ marginTop: '10px' }}
                  variant="outlined"
                  onClick={() => fileInputRef.current.click()} // Trigger file input click
                >
                  {addData?.image ? "Change Thumbnail" : "Add Thumbnail"}
                </Button>
              </div>
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Category:</label>
              <TextField
                value={addData?.category || ''}
                onChange={(e) => setAddData({ ...addData, category: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              />
            </div>
            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Active:</label>
              <TextField
                select
                value={addData?.active}
                onChange={(e) => setAddData({ ...addData, active: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              >
                <MenuItem value={true}>✅</MenuItem>
                <MenuItem value={false}>❌</MenuItem>
              </TextField>
            </div>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="outlined">
            Cancel
          </Button>
          <Button disabled={isUploading} onClick={handleAddNew} color="primary" variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAddV2} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add new photo pack V2</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>ID:</label>
              <TextField
                value={addData?.id || ''}
                onChange={(e) => setAddData({ ...addData, id: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              />
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Type:</label>
              <TextField
                select
                value={addData?.type || ''}
                onChange={(e) => setAddData({ ...addData, type: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              >
                <MenuItem value="ai_photography">AI Photography</MenuItem>
                <MenuItem value="ai_headshot">AI Headshot</MenuItem>
              </TextField>
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Show on Home page:</label>
              <TextField
                select
                value={addData?.showOnLanding || false}
                onChange={(e) => setAddData({ ...addData, showOnLanding: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              >
                <MenuItem value={true}>✅</MenuItem>
                <MenuItem value={false}>❌</MenuItem>
              </TextField>
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Hot:</label>
              <TextField
                select
                value={addData?.isHot || false}
                onChange={(e) => setAddData({ ...addData, isHot: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              >
                <MenuItem value={true}>✅</MenuItem>
                <MenuItem value={false}>❌</MenuItem>
              </TextField>
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Title:</label>
              <TextField
                value={addData?.title || ''}
                onChange={(e) => setAddData({ ...addData, title: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              />
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Description:</label>
              <TextField
                value={addData?.description || ''}
                onChange={(e) => setAddData({ ...addData, description: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              />
            </div>

            {/* <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Male Prompts:</label>
                <TextField
                  value={addData?.malePrompts || ''}
                  onChange={(e) => setAddData({ ...addData, malePrompts: e.target.value })}
                  fullWidth
                  multiline
                  rows={10}
                  variant="outlined"
                  margin="normal"
                />
              </div>

              <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Female Prompts:</label>
                <TextField
                  value={addData?.femalePrompts || ''}
                  onChange={(e) => setAddData({ ...addData, femalePrompts: e.target.value })}
                  fullWidth
                  multiline
                  rows={10}
                  variant="outlined"
                  margin="normal"
                />
              </div> */}

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Image:</label>
              <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                {isUploading ? (
                  <CircularProgress size={12} color="inherit" style={{ marginRight: '6px' }} />
                ) : (
                  addData?.image && (
                    <img src={addData.image} style={{ width: "200px", cursor: "pointer" }} alt="Thumbnail" />
                  )
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    await handleImageUpload(file);
                  }}
                  style={{ marginTop: '10px', display: 'none' }}
                  ref={fileInputRef}
                />
                <Button
                  style={{ marginTop: '10px' }}
                  variant="outlined"
                  onClick={() => fileInputRef.current.click()}
                >
                  {addData?.image ? "Change Thumbnail" : "Add Thumbnail"}
                </Button>
              </div>
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Category:</label>
              <TextField
                value={addData?.category || ''}
                onChange={(e) => setAddData({ ...addData, category: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              />
            </div>

            <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'column' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Active:</label>
              <TextField
                select
                value={addData?.active || false}
                onChange={(e) => setAddData({ ...addData, active: e.target.value })}
                fullWidth
                variant="outlined"
                margin="normal"
              >
                <MenuItem value={true}>✅</MenuItem>
                <MenuItem value={false}>❌</MenuItem>
              </TextField>
            </div>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="outlined">
            Cancel
          </Button>
          <Button
            disabled={isUploading}
            onClick={handleAddNewV2}
            color="primary"
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <PromptsDialog
        open={openMalePrompts}
        onClose={() => setOpenMalePrompts(false)}
        title="Male Prompts"
        styleIds={selectedPrompts}
        firebase={firebase}
      />
      <PromptsDialog
        open={openFemalePrompts}
        onClose={() => setOpenFemalePrompts(false)}
        title="Female Prompts"
        styleIds={selectedPrompts}
        firebase={firebase}
      />

      {isSpinning && <Spinner />}
    </>
  );
};

export default PhotoPacks;
